import { useState, useEffect, useRef } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders(extra = {}) {
  return {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function fetchAiUseTypes() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_use_types?select=*,ai_use_categories(name,display_order)&order=ai_use_categories(display_order).asc,label.asc`,
    { headers: apiHeaders() }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Failed to load AI use types (${response.status}): ${detail}`);
  }

  return response.json();
}

async function fetchCoursePermissions(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_ai_permissions?course_id=eq.${courseId}&select=*`,
    { headers: apiHeaders() }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Failed to load AI permissions (${response.status}): ${detail}`);
  }

  return response.json();
}

async function upsertPermission(courseId, aiUseTypeId, permitted, note) {
  const response = await fetch(
  `${SUPABASE_URL}/rest/v1/course_ai_permissions?on_conflict=course_id,ai_use_type_id`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates'
      }),
      body: JSON.stringify({
        course_id: courseId,
        ai_use_type_id: aiUseTypeId,
        permitted,
        note: note || null
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Failed to save AI permission (${response.status}): ${detail}`);
  }
}

export default function CourseAiPermissions({ course }) {
  const [aiUseTypes, setAiUseTypes] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState('');

  const noteTimersRef = useRef({});
  const activeSavesRef = useRef(0);
  const saveMessageTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError('');
    setSaveStatus('');

    Promise.all([
      fetchAiUseTypes(),
      fetchCoursePermissions(course.id)
    ])
      .then(([types, perms]) => {
        if (cancelled) return;

        setAiUseTypes(Array.isArray(types) ? types : []);

        const permMap = {};
        const noteMap = {};

        (Array.isArray(perms) ? perms : []).forEach(p => {
          permMap[p.ai_use_type_id] = p.permitted === true;
          noteMap[p.ai_use_type_id] = p.note || '';
        });

        setPermissions(permMap);
        setNotes(noteMap);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load AI permissions:', err);
        setError(err?.message || 'Failed to load AI permissions.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;

      Object.values(noteTimersRef.current).forEach(timerId => {
        window.clearTimeout(timerId);
      });
      noteTimersRef.current = {};

      if (saveMessageTimerRef.current) {
        window.clearTimeout(saveMessageTimerRef.current);
      }
    };
  }, [course.id]);

  function beginSave() {
    activeSavesRef.current += 1;

    if (saveMessageTimerRef.current) {
      window.clearTimeout(saveMessageTimerRef.current);
      saveMessageTimerRef.current = null;
    }

    setSaveStatus('Saving...');
  }

  function finishSave() {
    activeSavesRef.current = Math.max(0, activeSavesRef.current - 1);

    if (activeSavesRef.current === 0) {
      setSaveStatus('Saved');

      saveMessageTimerRef.current = window.setTimeout(() => {
        setSaveStatus('');
        saveMessageTimerRef.current = null;
      }, 1500);
    }
  }

  async function saveOne(typeId, permitted, note) {
    beginSave();
    setError('');

    try {
      await upsertPermission(
        course.id,
        typeId,
        permitted,
        note
      );
    } catch (err) {
      console.error('AI permission autosave failed:', err);
      setError(err?.message || 'Failed to save AI permissions.');
    } finally {
      finishSave();
    }
  }

  function handlePermissionChange(typeId, permitted) {
    setPermissions(prev => ({
      ...prev,
      [typeId]: permitted
    }));

    saveOne(
      typeId,
      permitted,
      notes[typeId] || ''
    );
  }

  function handleNoteChange(typeId, note) {
    setNotes(prev => ({
      ...prev,
      [typeId]: note
    }));

    if (noteTimersRef.current[typeId]) {
      window.clearTimeout(noteTimersRef.current[typeId]);
    }

    noteTimersRef.current[typeId] = window.setTimeout(() => {
      delete noteTimersRef.current[typeId];

      saveOne(
        typeId,
        permissions[typeId] === true,
        note
      );
    }, 600);
  }

  function handleNoteBlur(typeId) {
    if (!noteTimersRef.current[typeId]) return;

    window.clearTimeout(noteTimersRef.current[typeId]);
    delete noteTimersRef.current[typeId];

    saveOne(
      typeId,
      permissions[typeId] === true,
      notes[typeId] || ''
    );
  }

  if (loading) return <div style={styles.muted}>Loading...</div>;

  if (aiUseTypes.length === 0) return (
    <div>
      <h2 style={styles.subheading}>AI Permissions</h2>
      <div style={styles.muted}>
        No AI use types have been defined yet. Add them in General Settings.
      </div>
    </div>
  );

  return (
    <div>
      <div style={styles.headingRow}>
        <h2 style={styles.subheading}>AI Permissions</h2>

        {saveStatus && (
          <span style={{
            ...styles.saveStatus,
            ...(saveStatus === 'Saved' ? styles.saveStatusSaved : {})
          }}>
            {saveStatus}
          </span>
        )}
      </div>

      <p style={styles.muted}>
        Select which AI uses are permitted in this course. Students see the permitted
        list by default but can view the full list including non-permitted uses.
        Add an optional note to any permission for course-specific context.
        Changes are saved automatically.
      </p>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.list}>
        {aiUseTypes.map(type => {
          const isPermitted = permissions[type.id] === true;

          return (
            <div
              key={type.id}
              style={{
                ...styles.item,
                ...(isPermitted ? styles.itemPermitted : {})
              }}
            >
              <div style={styles.itemTop}>
                <div style={styles.itemLeft}>
                  <label style={styles.checkLabel}>
                    <input
                      type="checkbox"
                      checked={isPermitted}
                      onChange={e =>
                        handlePermissionChange(type.id, e.target.checked)
                      }
                      style={{ marginRight: '10px' }}
                    />

                    <div>
                      <div style={styles.itemName}>{type.label}</div>

                      {type.description && (
                        <div style={styles.itemDesc}>{type.description}</div>
                      )}
                    </div>
                  </label>
                </div>

                <span style={{
                  ...styles.pill,
                  ...(isPermitted ? styles.pillPermitted : styles.pillNot)
                }}>
                  {isPermitted ? 'Permitted' : 'Not permitted'}
                </span>
              </div>

              <div style={styles.noteRow}>
                <input
                  style={styles.noteInput}
                  type="text"
                  value={notes[type.id] || ''}
                  onChange={e => handleNoteChange(type.id, e.target.value)}
                  onBlur={() => handleNoteBlur(type.id)}
                  placeholder="Optional course-specific note..."
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  headingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px'
  },
  subheading: { margin: 0, fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 20px', lineHeight: '1.5' },
  saveStatus: { fontSize: '12px', color: '#66788a', fontWeight: '600' },
  saveStatusSaved: { color: '#216e45' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  list: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' },
  item: {
    border: '1px solid #d7dee5', borderRadius: '10px',
    padding: '14px 16px', background: 'white'
  },
  itemPermitted: {
    borderColor: '#b9dfc9', background: '#f7fdf9'
  },
  itemTop: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', gap: '12px', marginBottom: '10px'
  },
  itemLeft: { flex: 1 },
  checkLabel: {
    display: 'flex', alignItems: 'flex-start', cursor: 'pointer', gap: '0'
  },
  itemName: { fontWeight: '700', fontSize: '14px', color: '#1f2933' },
  itemDesc: { fontSize: '13px', color: '#66788a', marginTop: '3px', lineHeight: '1.4' },
  pill: {
    borderRadius: '999px', padding: '3px 10px',
    fontSize: '12px', fontWeight: '700', flexShrink: 0
  },
  pillPermitted: { background: '#e7f5ed', color: '#216e45' },
  pillNot: { background: '#faeaea', color: '#a12a2a' },
  noteRow: { marginTop: '4px' },
  noteInput: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '6px',
    padding: '7px 10px', fontSize: '13px', boxSizing: 'border-box',
    background: 'white', color: '#1f2933'
  }
};